import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/admin/AdminProtectedRoute';
import DoctorProtectedRoute from './components/doctor/DoctorProtectedRoute';
import NurseProtectedRoute from './components/nurse/NurseProtectedRoute';
import ErrorBoundary from './components/ui/ErrorBoundary';
import LoadingSpinner from './components/LoadingSpinner';
import LanguageSwitcher from './components/LanguageSwitcher';
import GlobalRecordingPill from './components/admin/GlobalRecordingPill';
import NovaVersaoAviso from './components/ui/NovaVersaoAviso';
import VerComoPacienteAviso from './components/ui/VerComoPacienteAviso';
import GuardaDaAbaIsolada from './components/ui/GuardaDaAbaIsolada';
import { startVaultRecovery } from './lib/vaultRecovery';
import { isSessaoIsolada } from './lib/supabaseClient';
import { lazyComRetry } from './lib/lazyComRetry';

// Lazy loading das páginas - Portal do Paciente
const LoginPage = lazyComRetry(() => import('./pages/auth/LoginPage'));
const RegisterPage = lazyComRetry(() => import('./pages/auth/RegisterPage'));
const PortalEntryPage = lazyComRetry(() => import('./pages/auth/PortalEntryPage'));
const VerComoPacientePage = lazyComRetry(() => import('./pages/auth/VerComoPacientePage'));
const CampaignFormPage = lazyComRetry(() => import('./pages/campaigns/CampaignFormPage'));
const Dashboard = lazyComRetry(() => import('./pages/Dashboard'));
const AppointmentsPage = lazyComRetry(() => import('./pages/scheduling/AppointmentsPage'));
const NewAppointmentPage = lazyComRetry(() => import('./pages/scheduling/NewAppointmentPage'));
const AppointmentDetailPage = lazyComRetry(() => import('./pages/scheduling/AppointmentDetailPage'));
const ProfilePage = lazyComRetry(() => import('./pages/patient/ProfilePage'));
// O app novo da paciente (direção B). Hoje só a EQUIPE entra, pelo "Portal do
// paciente" da ficha: a própria página confere no servidor e devolve quem não
// veio por ali ao portal de sempre.
const AppPacientePage = lazyComRetry(() => import('./pages/patient/AppPacientePage'));
const SettingsPage = lazyComRetry(() => import('./pages/patient/SettingsPage'));
const PatientDocumentsPage = lazyComRetry(() => import('./pages/patient/PatientDocumentsPage'));
const IntakeFormPage = lazyComRetry(() => import('./pages/patient/IntakeFormPage'));
const OnboardingWizard = lazyComRetry(() => import('./pages/patient/OnboardingWizard'));
const ShopPage = lazyComRetry(() => import('./pages/patient/ShopPage'));

// Lazy loading das páginas - Painel Admin
const AdminLoginPage = lazyComRetry(() => import('./pages/admin/AdminLoginPage'));
const AdminDashboard = lazyComRetry(() => import('./pages/admin/AdminDashboard'));
const CalendarPage = lazyComRetry(() => import('./pages/admin/CalendarPage'));
const AdminAppointmentsPage = lazyComRetry(() => import('./pages/admin/AdminAppointmentsPage'));
const PatientsPage = lazyComRetry(() => import('./pages/admin/PatientsPage'));
const AdminIntakesPage = lazyComRetry(() => import('./pages/admin/AdminIntakesPage'));
const PatientProfilePage = lazyComRetry(() => import('./pages/admin/PatientProfilePage'));
const ProvidersPage = lazyComRetry(() => import('./pages/admin/ProvidersPage'));
const AdminsPage = lazyComRetry(() => import('./pages/admin/AdminsPage'));
const NursesPage = lazyComRetry(() => import('./pages/admin/NursesPage'));
const MySchedulePage = lazyComRetry(() => import('./pages/admin/MySchedulePage'));
const NotificationRulesPage = lazyComRetry(() => import('./pages/admin/NotificationRulesPage'));
const ServicesPricingPage = lazyComRetry(() => import('./pages/admin/ServicesPricingPage'));
const PaymentsReportPage = lazyComRetry(() => import('./pages/admin/PaymentsReportPage'));
const AttendantsPage = lazyComRetry(() => import('./pages/admin/AttendantsPage'));
const AdminSettingsPage = lazyComRetry(() => import('./pages/admin/AdminSettingsPage'));
const JornadaConfiguracaoPage = lazyComRetry(() => import('./pages/admin/JornadaConfiguracaoPage'));
const HandoffSessionsPage = lazyComRetry(() => import('./pages/admin/HandoffSessionsPage'));
const FailedMessagesPage = lazyComRetry(() => import('./pages/admin/FailedMessagesPage'));
const EmailTemplatesPage = lazyComRetry(() => import('./pages/admin/EmailTemplatesPage'));
const LetterheadPage = lazyComRetry(() => import('./pages/admin/LetterheadPage'));
const SupportPage = lazyComRetry(() => import('./pages/admin/SupportPage'));
const OptiMantraAgentPage = lazyComRetry(() => import('./pages/admin/OptiMantraAgentPage'));
const KnowledgeBasePage = lazyComRetry(() => import('./pages/admin/KnowledgeBasePage'));
const EssentiaAtendimentoPage = lazyComRetry(() => import('./pages/admin/EssentiaAtendimentoPage'));
const PipelinePage = lazyComRetry(() => import('./pages/admin/PipelinePage'));
const AniversariosPage = lazyComRetry(() => import('./pages/admin/AniversariosPage'));
const IntegrationsPage = lazyComRetry(() => import('./pages/admin/IntegrationsPage'));
const AtualizacoesPage = lazyComRetry(() => import('./pages/admin/AtualizacoesPage'));
const ManualPage = lazyComRetry(() => import('./pages/admin/ManualPage'));
const FullscriptCallbackPage = lazyComRetry(() => import('./pages/admin/FullscriptCallbackPage'));
const ArtifactFormsPage = lazyComRetry(() => import('./pages/admin/ArtifactFormsPage'));
const ArtifactResponsesPage = lazyComRetry(() => import('./pages/admin/ArtifactResponsesPage'));
const DoctorPatientsPage = lazyComRetry(() => import('./pages/doctor/DoctorPatientsPage'));

// Painel da Enfermagem (tablet-first; protegido por NurseProtectedRoute)
const NurseTodayPage = lazyComRetry(() => import('./pages/nurse/NurseTodayPage'));
const NursePatientPage = lazyComRetry(() => import('./pages/nurse/NursePatientPage'));

// Public pages (no auth)
const SharedArtifactPage = lazyComRetry(() => import('./pages/SharedArtifactPage'));

// Smart redirect: admins/providers vão para sua área, pacientes ficam no dashboard.
// Uses sessionStorage flag set by LoginPage (/login) so the patient portal session
// persists across ALL navigations — not just ?as=patient (which was ephemeral).
const HomeDashboard: React.FC = () => {
  const { profile } = useAuth();
  const [searchParams] = useSearchParams();

  // ?as=patient from URL (e.g. Google OAuth redirect) also sets the flag
  if (searchParams.get('as') === 'patient') {
    sessionStorage.setItem('patientPortalSession', 'true');
  }

  // Aba/pop-up "Portal do paciente" (sessão isolada) é sessão de paciente por
  // definição — no pop-up embutido o sessionStorage é o do admin, não dá pra usar.
  const isPatientSession = sessionStorage.getItem('patientPortalSession') === 'true' || isSessaoIsolada();

  // Enfermeira vai SEMPRE pro painel dela, mesmo logando pela porta do paciente
  // (/login seta patientPortalSession). Ela não tem uso no portal do paciente.
  // Incidente 24/jun: Leilane entrou pela raiz e ficou presa no dashboard de paciente.
  if (profile?.role === 'nurse') return <Navigate to="/nurse" replace />;

  if (!isPatientSession) {
    if (profile?.role === 'admin') return <Navigate to="/admin" replace />;
    if (profile?.role === 'provider') return <Navigate to="/doctor" replace />;
  }

  return <Dashboard />;
};

const App: React.FC = () => {
  const { t } = useTranslation();
  const { loading } = useAuth();
  const location = useLocation();

  // Cofre de gravação: no boot (e a cada 5min), devolve ao servidor pedaços de
  // áudio que ficaram presos no IndexedDB quando a rede caiu e a aba morreu.
  // Caso Sandra 31/08 — a consulta inteira estava no navegador e se perdeu.
  useEffect(() => startVaultRecovery(), []);
  // Painel da enfermeira (tablet): o switcher flutuante sobrepõe o header — esconder lá
  const isNurseArea = location.pathname.startsWith('/nurse');

  if (loading) {
    return <LoadingSpinner fullScreen message={t('common.loading')} />;
  }

  return (
    <ErrorBoundary>
      {!isNurseArea && <LanguageSwitcher />}
      {/* Selo flutuante de gravação ativa — visível em qualquer página do portal */}
      <GlobalRecordingPill />
      {/* Aba aberta há dias com código velho (incidente 14/set) — pede para recarregar */}
      <NovaVersaoAviso />
      {/* Aba aberta pela ficha com a sessão de um paciente ("Portal do paciente") */}
      <VerComoPacienteAviso />
      {/* ...e essa mesma aba volta a ser do admin se ele for trabalhar nela */}
      <GuardaDaAbaIsolada />
      <Suspense fallback={<LoadingSpinner fullScreen />}>
        <Routes>
        {/* Rotas públicas */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        {/* Entrada pelo link do e-mail (migration 190): a posse da caixa é a
            autenticação. Página-ponte com botão — scanner de e-mail faz GET,
            não clica, então a sessão só nasce no clique do paciente. */}
        <Route path="/e/:token" element={<PortalEntryPage />} />
        {/* Admin vendo o portal como um paciente: token no fragmento (#th=),
            aba isolada (lib/supabaseClient.ts). Rota do webhook: /api/admin/portal-as-patient. */}
        <Route path="/ver-como" element={<VerComoPacientePage />} />
        <Route path="/shared/:token" element={<SharedArtifactPage />} />
        {/* /intake era a página demo pré-pagamento; gravava intake REAL sem cobrar
            (caso Rogério 03/ago — preencheu tudo sem pagar). O funil oficial é /start. */}
        <Route path="/intake" element={<Navigate to="/start" replace />} />
        <Route path="/start" element={<OnboardingWizard />} />
        {/* Motor de Aquisição (S4): form público de qualificação por campanha.
            As landings de anúncio linkam pra cá; o POST vai pro webhook
            (/api/leads), nunca direto na tabela. */}
        <Route path="/c/:campaignId" element={<CampaignFormPage />} />

        {/* Rotas protegidas */}
        <Route path="/" element={
          <ProtectedRoute>
            <HomeDashboard />
          </ProtectedRoute>
        } />

        <Route path="/app" element={
          <ProtectedRoute>
            <AppPacientePage />
          </ProtectedRoute>
        } />

        <Route path="/appointments" element={
          <ProtectedRoute>
            <AppointmentsPage />
          </ProtectedRoute>
        } />

        <Route path="/appointments/new" element={
          <ProtectedRoute>
            <NewAppointmentPage />
          </ProtectedRoute>
        } />

        <Route path="/appointments/:id" element={
          <ProtectedRoute>
            <AppointmentDetailPage />
          </ProtectedRoute>
        } />

        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        } />

        <Route path="/services" element={
          <ProtectedRoute>
            <ShopPage />
          </ProtectedRoute>
        } />

        {/* Meu tratamento virou a primeira ABA de /profile (17/09/2026): uma
            página só para o paciente. A rota antiga continua valendo — é
            destino de link de e-mail (portalAccessLink) e do que já foi
            enviado — e leva direto à aba. */}
        <Route path="/treatments" element={<Navigate to="/profile?tab=tratamento" replace />} />

        <Route path="/settings" element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        } />

        <Route path="/documents" element={
          <ProtectedRoute>
            <PatientDocumentsPage />
          </ProtectedRoute>
        } />

        <Route path="/forms/:documentId" element={
          <ProtectedRoute>
            <IntakeFormPage />
          </ProtectedRoute>
        } />

        {/* Rotas Admin - Públicas */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/fullscript/callback" element={<FullscriptCallbackPage />} />

        {/* Rotas Admin - Protegidas */}
        <Route path="/admin" element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/calendar" element={
          <AdminProtectedRoute>
            <CalendarPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/integrations" element={
          <AdminProtectedRoute>
            <IntegrationsPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/atualizacoes" element={
          <AdminProtectedRoute>
            <AtualizacoesPage />
          </AdminProtectedRoute>
        } />

        {/* Manual: como se faz cada coisa no portal. Vale para toda a equipe,
            por isso fica na proteção comum de /admin e num link do rodapé do
            menu — não numa seção que só o admin abre. */}
        <Route path="/admin/manual" element={
          <AdminProtectedRoute>
            <ManualPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/appointments" element={
          <AdminProtectedRoute>
            <AdminAppointmentsPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/patients" element={
          <AdminProtectedRoute>
            <PatientsPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/patients/:id" element={
          <AdminProtectedRoute>
            <PatientProfilePage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/intakes" element={
          <AdminProtectedRoute>
            <AdminIntakesPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/artifacts" element={
          <AdminProtectedRoute>
            <ArtifactFormsPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/artifacts/:id/responses" element={
          <AdminProtectedRoute>
            <ArtifactResponsesPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/providers" element={
          <AdminProtectedRoute>
            <ProvidersPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/admins" element={
          <AdminProtectedRoute>
            <AdminsPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/nurses" element={
          <AdminProtectedRoute>
            <NursesPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/my-schedule" element={
          <AdminProtectedRoute>
            <MySchedulePage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/services" element={
          <AdminProtectedRoute>
            <ServicesPricingPage />
          </AdminProtectedRoute>
        } />

        {/* Tela antiga de preços — unificada como aba (23/jul) */}
        <Route path="/admin/pricing" element={<Navigate to="/admin/services?tab=pricing" replace />} />
        {/* A conexão do WhatsApp mora em Atendimento › Configurações › Conexão WhatsApp. /admin/whatsapp nunca
            existiu como tela e caía no Dashboard, justo o "Reconectar" do dia em que o WhatsApp cai (30/09). */}
        <Route path="/admin/whatsapp" element={<Navigate to="/admin/essentia-atendimento?tab=settings&sub=whatsapp" replace />} />

        <Route path="/admin/payments" element={
          <AdminProtectedRoute>
            <PaymentsReportPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/attendants" element={
          <AdminProtectedRoute>
            <AttendantsPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/handoff" element={
          <AdminProtectedRoute>
            <HandoffSessionsPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/settings" element={
          <AdminProtectedRoute>
            <AdminSettingsPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/jornada/configuracao" element={
          <AdminProtectedRoute>
            <JornadaConfiguracaoPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/failed-messages" element={
          <AdminProtectedRoute>
            <FailedMessagesPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/email-templates" element={
          <AdminProtectedRoute>
            <EmailTemplatesPage />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/letterhead" element={
          <AdminProtectedRoute>
            <LetterheadPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/optimantra" element={
          <AdminProtectedRoute>
            <OptiMantraAgentPage />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/optimantra/:convId" element={
          <AdminProtectedRoute>
            <OptiMantraAgentPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/knowledge-base" element={
          <AdminProtectedRoute>
            <KnowledgeBasePage />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/knowledge-base/:convId" element={
          <AdminProtectedRoute>
            <KnowledgeBasePage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/essentia-atendimento" element={
          <AdminProtectedRoute>
            <EssentiaAtendimentoPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/pipeline" element={
          <AdminProtectedRoute>
            <PipelinePage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/aniversarios" element={
          <AdminProtectedRoute>
            <AniversariosPage />
          </AdminProtectedRoute>
        } />

        <Route path="/admin/support" element={
          <AdminProtectedRoute>
            <SupportPage />
          </AdminProtectedRoute>
        } />

        {/* Rotas Médico - Públicas */}
        <Route path="/doctor/login" element={<AdminLoginPage />} />

        {/* Rotas Médico - Protegidas */}
        <Route path="/doctor" element={
          <DoctorProtectedRoute>
            <AdminDashboard />
          </DoctorProtectedRoute>
        } />

        <Route path="/doctor/calendar" element={
          <DoctorProtectedRoute>
            <CalendarPage />
          </DoctorProtectedRoute>
        } />

        <Route path="/doctor/appointments" element={
          <DoctorProtectedRoute>
            <AdminAppointmentsPage />
          </DoctorProtectedRoute>
        } />

        <Route path="/doctor/my-schedule" element={
          <DoctorProtectedRoute>
            <MySchedulePage />
          </DoctorProtectedRoute>
        } />

        <Route path="/doctor/notifications" element={
          <DoctorProtectedRoute>
            <NotificationRulesPage />
          </DoctorProtectedRoute>
        } />

        <Route path="/doctor/support" element={
          <DoctorProtectedRoute>
            <SupportPage />
          </DoctorProtectedRoute>
        } />
        <Route path="/doctor/letterhead" element={
          <DoctorProtectedRoute>
            <LetterheadPage />
          </DoctorProtectedRoute>
        } />

        <Route path="/doctor/patients" element={
          <DoctorProtectedRoute>
            <PatientsPage />
          </DoctorProtectedRoute>
        } />

        <Route path="/doctor/patient/:id" element={
          <DoctorProtectedRoute>
            <PatientProfilePage />
          </DoctorProtectedRoute>
        } />

        <Route path="/doctor/knowledge-base" element={
          <DoctorProtectedRoute>
            <KnowledgeBasePage />
          </DoctorProtectedRoute>
        } />
        <Route path="/doctor/knowledge-base/:convId" element={
          <DoctorProtectedRoute>
            <KnowledgeBasePage />
          </DoctorProtectedRoute>
        } />

        {/* Painel da Enfermagem — protegido (admin/provider; role 'nurse' na etapa 2) */}
        <Route path="/nurse/login" element={<AdminLoginPage />} />
        <Route path="/nurse" element={
          <NurseProtectedRoute>
            <NurseTodayPage />
          </NurseProtectedRoute>
        } />
        <Route path="/nurse/patient/:id" element={
          <NurseProtectedRoute>
            <NursePatientPage />
          </NurseProtectedRoute>
        } />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default App;
